import React from "react";
import {
  Alert,
  Box,
  Button,
  Chip,
  Divider,
  MenuItem,
  Paper,
  Stack,
  TextField,
  Typography,
} from "@mui/material";
import AccountBalanceWalletRoundedIcon from "@mui/icons-material/AccountBalanceWalletRounded";
import CalendarMonthRoundedIcon from "@mui/icons-material/CalendarMonthRounded";
import EventAvailableRoundedIcon from "@mui/icons-material/EventAvailableRounded";
import FlagRoundedIcon from "@mui/icons-material/FlagRounded";
import GroupsRoundedIcon from "@mui/icons-material/GroupsRounded";
import SavingsRoundedIcon from "@mui/icons-material/SavingsRounded";
import TaskAltRoundedIcon from "@mui/icons-material/TaskAltRounded";
import TrendingUpRoundedIcon from "@mui/icons-material/TrendingUpRounded";
import { useNavigate } from "react-router-dom";
import { ownerFinanceStatsApi } from "../../../../api/ownerFinanceStats";
import { formatApiError } from "../../../../api/client";
import DashboardLoadingState from "../../../../components/dashboard/DashboardLoadingState";
import { compactActionSx, dateTime, money, surfaceSx } from "../center/ownerCenterUi";
import OwnerCashRunwayChart from "./OwnerCashRunwayChart";

function currentMonth() {
  const now = new Date();
  return `${now.getFullYear()}-${String(now.getMonth() + 1).padStart(2, "0")}`;
}

function monthLabel(value) {
  const [year, month] = String(value || "").split("-").map(Number);
  if (!year || !month) return "Mes";
  const label = new Intl.DateTimeFormat("es-AR", { month: "long", year: "numeric" }).format(new Date(year, month - 1, 1));
  return `${label.charAt(0).toUpperCase()}${label.slice(1)}`;
}

function dayLabel(value) {
  if (!value) return "—";
  const date = new Date(`${value}T12:00:00`);
  if (Number.isNaN(date.getTime())) return "—";
  return new Intl.DateTimeFormat("es-AR", { weekday: "short", day: "2-digit", month: "short" }).format(date);
}

function minutes(value) {
  const total = Math.max(0, Number(value || 0));
  const hours = Math.floor(total / 60);
  const mins = Math.round(total % 60);
  return hours ? `${hours} h${mins ? ` ${mins} min` : ""}` : `${mins} min`;
}

function Metric({ label, value, helper, emphasis = false }) {
  return (
    <Box sx={{ minWidth: 0, p: 1.55, borderRadius: 2.5, border: "1px solid", borderColor: emphasis ? "primary.main" : "divider", bgcolor: emphasis ? "rgba(var(--wp-primary-rgb,25,118,210),.055)" : "rgba(var(--wp-panel-ink-rgb,25,36,47),.022)" }}>
      <Typography sx={{ color: "text.secondary", fontSize: 11.5, fontWeight: 900 }}>{label}</Typography>
      <Typography sx={{ mt: .3, fontWeight: 1000, fontSize: { xs: 21, xl: 27 }, letterSpacing: "-.04em", overflowWrap: "anywhere" }}>{value}</Typography>
      {helper ? <Typography sx={{ mt: .3, color: "text.secondary", fontSize: 11.5, fontWeight: 700, lineHeight: 1.4 }}>{helper}</Typography> : null}
    </Box>
  );
}

function StatusLine({ title, value, helper }) {
  return (
    <Stack direction="row" justifyContent="space-between" spacing={2} alignItems="flex-start" sx={{ py: .9 }}>
      <Box sx={{ minWidth: 0 }}><Typography sx={{ fontWeight: 900, fontSize: 13 }}>{title}</Typography>{helper ? <Typography sx={{ color: "text.secondary", fontWeight: 700, fontSize: 11.5, mt: .15 }}>{helper}</Typography> : null}</Box>
      <Typography sx={{ fontWeight: 1000, fontSize: 13.5, textAlign: "right", flexShrink: 0 }}>{value}</Typography>
    </Stack>
  );
}

export default function OwnerFinanceFutureView() {
  const navigate = useNavigate();
  const [month, setMonth] = React.useState(currentMonth());
  const [currency, setCurrency] = React.useState("ARS");
  const [future, setFuture] = React.useState(null);
  const [desiredNet, setDesiredNet] = React.useState("0");
  const [loading, setLoading] = React.useState(true);
  const [saving, setSaving] = React.useState(false);
  const [error, setError] = React.useState("");

  const load = React.useCallback(async () => {
    setError("");
    try {
      const response = await ownerFinanceStatsApi.future({ month, currency, tzOffsetMinutes: new Date().getTimezoneOffset() });
      setFuture(response.future);
      setDesiredNet(String(response.future?.target?.desiredMonthlyNet || 0));
      if (response.future?.currency && response.future.currency !== currency) setCurrency(response.future.currency);
    } catch (err) {
      setError(formatApiError(err, "No pude construir tu escenario futuro."));
    } finally {
      setLoading(false);
    }
  }, [month, currency]);

  React.useEffect(() => { setLoading(true); load(); }, [load]);

  const saveTarget = async () => {
    setSaving(true);
    setError("");
    try {
      const response = await ownerFinanceStatsApi.updateFutureTarget({ month, currency, desiredMonthlyNet: Number(desiredNet || 0), tzOffsetMinutes: new Date().getTimezoneOffset() });
      setFuture(response.future);
      setDesiredNet(String(response.future?.target?.desiredMonthlyNet || 0));
    } catch (err) {
      setError(formatApiError(err, "No pude guardar el excedente deseado."));
    } finally {
      setSaving(false);
    }
  };

  if (loading && !future) return <DashboardLoadingState label="Calculando caja, vencimientos y capacidad…" minHeight={560} />;

  const snapshot = future?.snapshot || {};
  const pace = future?.pace || {};
  const cash = future?.cashPlan || null;
  const actions = future?.commercial?.nextActions || [];
  const criticalCapacity = cash?.capacityUntilDeadline || {};
  const criticalGap = Number(cash?.requiredBeforeDeadline || 0);
  const monthlyGap = Number(snapshot.uncoveredGap || 0);
  const tension = Boolean(cash?.tensionDate);
  const upcoming = (cash?.rows || [])
    .flatMap((row) => (row.events || []).map((event) => ({ ...event, date: row.date })))
    .filter((event) => event.kind !== "historical_variable")
    .slice(0, 8);

  return (
    <Stack spacing={1.45}>
      {error ? <Alert severity="error">{error}</Alert> : null}

      <Paper variant="outlined" sx={{ ...surfaceSx, p: { xs: 1.6, lg: 2 } }}>
        <Stack direction={{ xs: "column", xl: "row" }} justifyContent="space-between" spacing={1.5} alignItems={{ xl: "flex-end" }}>
          <Box sx={{ maxWidth: 820 }}>
            <Stack direction="row" spacing={.7} alignItems="center" flexWrap="wrap">
              <Chip size="small" icon={<TrendingUpRoundedIcon />} label="Plan de caja" sx={{ fontWeight: 950 }} />
              <Typography sx={{ fontWeight: 1000, fontSize: { xs: 25, lg: 33 }, letterSpacing: "-.045em" }}>Futuro / Metas</Typography>
            </Stack>
            <Typography sx={{ mt: .45, color: "text.secondary", fontWeight: 700, lineHeight: 1.5 }}>
              Parte del dinero que tenés hoy, coloca vencimientos e ingresos en sus fechas y usa tu agenda para decir <strong>cuánto necesitás asegurar y antes de cuándo</strong>. El ritmo mensual normal se muestra aparte para no confundir urgencia con estilo de vida.
            </Typography>
          </Box>
          <Stack direction={{ xs: "column", sm: "row" }} spacing={.75}>
            <TextField size="small" type="month" label="Mes" value={month} onChange={(event) => setMonth(event.target.value)} InputLabelProps={{ shrink: true }} sx={{ minWidth: 150 }} />
            <TextField select size="small" label="Moneda" value={currency} onChange={(event) => setCurrency(event.target.value)} sx={{ minWidth: 105 }}>
              {(future?.currencies || ["ARS"]).map((row) => <MenuItem key={row} value={row}>{row}</MenuItem>)}
            </TextField>
            <TextField size="small" type="number" label="Quiero acumular / mes" value={desiredNet} onChange={(event) => setDesiredNet(event.target.value)} inputProps={{ min: 0, step: 1000 }} sx={{ minWidth: 190 }} />
            <Button variant="contained" onClick={saveTarget} disabled={saving} sx={compactActionSx}>{saving ? "Guardando…" : "Aplicar"}</Button>
          </Stack>
        </Stack>
      </Paper>

      {!cash ? (
        <Alert severity="info">La proyección de caja se calcula para el mes actual o meses futuros. Elegí un período vigente para ver dinero disponible, vencimientos y fecha crítica.</Alert>
      ) : (
        <>
          <Box sx={{ display: "grid", gridTemplateColumns: { xs: "1fr", sm: "repeat(2,1fr)", xl: "repeat(4,1fr)" }, gap: 1 }}>
            <Metric label="Dinero disponible hoy" value={money(cash.availableBalance, currency)} helper={`${cash.accountCount || 0} cajas/bancos/billeteras · saldo calculado`} emphasis />
            <Metric label="Gastos fijos conocidos" value={money(cash.knownFixedExpense, currency)} helper={`${monthLabel(month)} · respetan el día de cada mensual`} />
            <Metric label={tension ? "Fecha de tensión" : "Caja mínima proyectada"} value={tension ? dayLabel(cash.tensionDate) : money(cash.minimumBalance, currency)} helper={tension ? "Primer día en que la caja proyectada cruza $0" : `Mínimo estimado hacia ${dayLabel(cash.minimumDate)}`} />
            <Metric label={tension ? "Necesitás asegurar antes de esa fecha" : "Faltante para tu meta mensual"} value={money(tension ? criticalGap : monthlyGap, currency)} helper={tension ? "Monto mínimo adicional para no entrar en negativo" : "Aunque la caja aguante, esto falta para cumplir tu objetivo de acumulación"} />
          </Box>

          <Paper variant="outlined" sx={{ ...surfaceSx, p: { xs: 1.4, lg: 2 } }}>
            <Stack direction={{ xs: "column", md: "row" }} justifyContent="space-between" spacing={1} alignItems={{ md: "flex-start" }} sx={{ mb: 1 }}>
              <Box>
                <Typography sx={{ fontWeight: 1000, fontSize: 20 }}>Trayectoria de caja real</Typography>
                <Typography sx={{ mt: .2, color: "text.secondary", fontWeight: 700, fontSize: 12.5, maxWidth: 820 }}>Arranca en tus cuentas de hoy. Suma cobros comprometidos, resta mensuales en su fecha y distribuye el gasto cotidiano según el patrón de tus meses anteriores. La línea de $0 marca el límite que no queremos cruzar.</Typography>
              </Box>
              <Stack direction="row" spacing={.6} flexWrap="wrap" useFlexGap>
                {(cash.accounts || []).slice(0, 4).map((account) => <Chip key={account.id} size="small" label={`${account.name}: ${money(account.balance, currency)}`} variant="outlined" sx={{ fontWeight: 850 }} />)}
              </Stack>
            </Stack>
            <OwnerCashRunwayChart rows={cash.rows || []} currency={currency} />
            <Typography sx={{ mt: .9, color: "text.secondary", fontSize: 11.5, fontWeight: 700 }}>{cash.note} El saldo inicial depende de que tus cuentas estén conciliadas.</Typography>
          </Paper>

          <Box sx={{ display: "grid", gridTemplateColumns: { xs: "1fr", xl: "minmax(0,1.05fr) minmax(400px,.95fr)" }, gap: 1.3, alignItems: "start" }}>
            <Stack spacing={1.3}>
              <Paper variant="outlined" sx={{ ...surfaceSx, p: 1.75 }}>
                <Stack direction="row" justifyContent="space-between" alignItems="center"><Box><Typography sx={{ fontWeight: 1000, fontSize: 18 }}>Qué tenés que hacer matemáticamente</Typography><Typography sx={{ color: "text.secondary", fontWeight: 700, fontSize: 12 }}>Primero protegemos la caja. Después miramos la meta mensual.</Typography></Box><EventAvailableRoundedIcon color="action" /></Stack>
                <Divider sx={{ my: 1.1 }} />
                {tension ? (
                  <>
                    <Typography sx={{ fontWeight: 1000, fontSize: { xs: 24, md: 31 }, letterSpacing: "-.04em" }}>Asegurar {money(criticalGap, currency)} antes del {dayLabel(cash.deadline)}.</Typography>
                    <Typography sx={{ mt: .45, color: "text.secondary", fontWeight: 700, lineHeight: 1.5 }}>Éste es tu deadline económico real con los datos cargados. No es “ganar eso cada día”: podés cubrirlo con uno o varios cobros, trabajos o ventas antes de esa fecha.</Typography>
                    <Box sx={{ display: "grid", gridTemplateColumns: { xs: "1fr", sm: "repeat(3,1fr)" }, gap: .8, mt: 1.2 }}>
                      <Metric label="Días laborales hasta ahí" value={criticalCapacity.workdays || 0} helper="Según tu jornada configurada" />
                      <Metric label="Horas libres medidas" value={minutes(criticalCapacity.freeMinutes)} helper="Después de agenda y rutinas" />
                      <Metric label="Referencia por día disponible" value={money(cash.criticalPace?.perWorkday, currency)} helper="Sólo una unidad de planificación, no una tarifa" />
                    </Box>
                  </>
                ) : (
                  <Alert severity="success">Con la caja y compromisos cargados no aparece un cruce bajo cero hasta {dayLabel(cash.endDate)}. Tu siguiente objetivo no es “sobrevivir al mes”, sino cubrir la brecha de acumulación de {money(monthlyGap, currency)}.</Alert>
                )}
                {criticalCapacity.unsizedTasks > 0 ? <Typography sx={{ mt: .8, color: "text.secondary", fontWeight: 750, fontSize: 11.5 }}>{criticalCapacity.unsizedTasks} tarea(s) en ese período no tienen duración; tus horas libres pueden estar sobreestimadas.</Typography> : null}
              </Paper>

              <Paper variant="outlined" sx={{ ...surfaceSx, p: 1.75 }}>
                <Stack direction="row" justifyContent="space-between" alignItems="center"><Box><Typography sx={{ fontWeight: 1000, fontSize: 18 }}>Ritmo sano de un mes completo</Typography><Typography sx={{ color: "text.secondary", fontWeight: 700, fontSize: 12 }}>Éste sí es el número para mirar hacia adelante, sin importar si hoy es día 2 o día 30.</Typography></Box><FlagRoundedIcon color="action" /></Stack>
                <Divider sx={{ my: 1.1 }} />
                <Box sx={{ display: "grid", gridTemplateColumns: { xs: "1fr", sm: "repeat(3,1fr)" }, gap: .8 }}>
                  <Metric label="Ingreso mensual objetivo" value={money(snapshot.requiredIncome, currency)} helper={`Costo de referencia ${money(snapshot.baselineExpense, currency)} + acumulación ${money(snapshot.desiredMonthlyNet, currency)}`} emphasis />
                  <Metric label="Referencia semanal" value={money(pace.healthyPerWeek, currency)} helper="Repartido sobre una semana laboral normal" />
                  <Metric label="Referencia por día útil" value={money(pace.healthyPerWorkday, currency)} helper="No exige facturar todos los días; traduce el objetivo" />
                </Box>
                <Typography sx={{ mt: .9, color: "text.secondary", fontWeight: 700, fontSize: 11.7, lineHeight: 1.5 }}>La meta real sigue siendo asegurar {money(snapshot.requiredIncome, currency)} durante el mes. Un trabajo grande puede cubrir varios días de referencia de una sola vez.</Typography>
              </Paper>

              <Paper variant="outlined" sx={{ ...surfaceSx, p: 1.75 }}>
                <Typography sx={{ fontWeight: 1000, fontSize: 18 }}>Próximos movimientos que ya conocemos</Typography>
                <Typography sx={{ color: "text.secondary", fontWeight: 700, fontSize: 12 }}>Vencimientos y cobros seguros con fecha. El gasto cotidiano estimado está en la curva, pero no ensucia esta lista.</Typography>
                <Divider sx={{ my: 1.1 }} />
                {upcoming.length ? <Stack divider={<Divider flexItem />}>{upcoming.map((event) => <StatusLine key={event.id} title={event.label} value={`${event.direction === "income" ? "+" : "−"}${money(event.amount, currency)}`} helper={dayLabel(event.date)} />)}</Stack> : <Typography sx={{ color: "text.secondary", fontWeight: 700, fontSize: 12.5 }}>No hay vencimientos o cobros fechados en el tramo visible.</Typography>}
              </Paper>
            </Stack>

            <Stack spacing={1.3} sx={{ position: { xl: "sticky" }, top: { xl: 88 } }}>
              <Paper variant="outlined" sx={{ ...surfaceSx, p: 1.75 }}>
                <Stack direction="row" justifyContent="space-between" alignItems="center"><Box><Typography sx={{ fontWeight: 1000, fontSize: 18 }}>De dónde puede salir el dinero</Typography><Typography sx={{ color: "text.secondary", fontWeight: 700, fontSize: 12 }}>Separado por certeza.</Typography></Box><GroupsRoundedIcon color="action" /></Stack>
                <Divider sx={{ my: 1.1 }} />
                <StatusLine title="Ya ingresó este mes" value={money(snapshot.actual?.income, currency)} helper="Movimientos reales" />
                <StatusLine title="Mensuales por cobrar" value={money(snapshot.remainingRecurringIncome, currency)} helper="Esperados y todavía no registrados" />
                <StatusLine title="Trabajos comprometidos" value={money(snapshot.committedCommercial, currency)} helper="Aceptados / en curso / completados con fecha" />
                <StatusLine title="Presupuestos abiertos" value={money(snapshot.quotedCommercial, currency)} helper="Posibles, no contados como seguros" />
                <Divider sx={{ my: .6 }} />
                <StatusLine title="Brecha mensual segura" value={money(snapshot.uncoveredGap, currency)} helper={snapshot.gapAfterQuotes < snapshot.uncoveredGap ? `Si cerraran todos los presupuestos: ${money(snapshot.gapAfterQuotes, currency)}` : "Todavía no cubierta"} />
              </Paper>

              <Paper variant="outlined" sx={{ ...surfaceSx, p: 1.75 }}>
                <Stack direction="row" justifyContent="space-between" alignItems="center"><Box><Typography sx={{ fontWeight: 1000, fontSize: 18 }}>Qué hacer ahora</Typography><Typography sx={{ color: "text.secondary", fontWeight: 700, fontSize: 12 }}>CRM y agenda conectados con la brecha.</Typography></Box><TaskAltRoundedIcon color="action" /></Stack>
                <Divider sx={{ my: 1.1 }} />
                {actions.length ? <Stack divider={<Divider flexItem />}>{actions.slice(0, 6).map((row) => <Box key={row.id} sx={{ py: .85 }}><Typography sx={{ fontWeight: 900, fontSize: 13 }}>{row.title}</Typography><Typography sx={{ mt: .15, color: "text.secondary", fontWeight: 700, fontSize: 11.5 }}>{[row.client?.name, row.sale?.title].filter(Boolean).join(" · ") || "Seguimiento comercial"}</Typography><Typography sx={{ mt: .25, color: "text.secondary", fontWeight: 750, fontSize: 11.5 }}>{row.scheduledStartAt ? `Agendado ${dateTime(row.scheduledStartAt)}` : row.dueAt ? `Vence ${dateTime(row.dueAt)}` : "Falta definir fecha"}</Typography></Box>)}</Stack> : <Typography sx={{ color: "text.secondary", fontWeight: 700, fontSize: 12.5 }}>No hay próximos pasos comerciales cargados. Si existe brecha, cargá prospectos/propuestas o definí seguimientos.</Typography>}
                <Button fullWidth startIcon={<CalendarMonthRoundedIcon />} variant="contained" onClick={() => navigate("/dashboard/owner/calendario")} sx={{ ...compactActionSx, mt: 1.2 }}>Abrir calendario y ejecutar</Button>
              </Paper>

              <Paper variant="outlined" sx={{ ...surfaceSx, p: 1.5, bgcolor: "rgba(var(--wp-primary-rgb,25,118,210),.035)" }}>
                <Stack direction="row" spacing={.7} alignItems="center"><AccountBalanceWalletRoundedIcon fontSize="small" /><Typography sx={{ fontWeight: 950, fontSize: 12.5 }}>Calidad del cálculo</Typography></Stack>
                <Typography sx={{ mt: .45, color: "text.secondary", fontWeight: 700, fontSize: 11.5, lineHeight: 1.5 }}>Caja: cuentas reales calculadas. Fijos: mensuales vigentes con día. Variables: patrón histórico de {cash.historicalProfileMonths || 0} mes(es). Comercial: sólo trabajo comprometido entra como seguro. Conciliar tus billeteras mejora directamente esta proyección.</Typography>
                {cash.investedBalance > 0 ? <Typography sx={{ mt: .55, fontWeight: 850, fontSize: 11.5 }}>Inversiones aparte: {money(cash.investedBalance, currency)}. No se usan como caja disponible.</Typography> : null}
              </Paper>
            </Stack>
          </Box>
        </>
      )}
    </Stack>
  );
}
